import { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { vinkStand, meldingPeil, meldingAantal } from '../lib/meldingsleutel.js';

const DAG_LABELS = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };
const DIENST_LABELS = {
  dagdienst: 'Dagdienst',
  late_dienst: 'Late dienst',
  late_dienst_dw: 'Late dienst',
  late_dienst_we: 'Late dienst',
  flexibel: 'Flexibele dienst',
};
const GEMEENTE_LABELS = { arnhem: 'Arnhem', rheden: 'Rheden', lingewaard: 'Lingewaard' };
const gemeenteNaam = (gemKey) =>
  GEMEENTE_LABELS[gemKey] || (gemKey ? gemKey.charAt(0).toUpperCase() + gemKey.slice(1) : '');

// "do 13 augustus" als de kalenderdatum bekend is, anders alleen "Do".
// De datum wordt in de RENDERLAAG op het signaal gezet (Weekrooster.jsx, meldingDatum) —
// kalenderdatums horen niet bij de engine, zelfde afspraak als bij de reeks-melding
// hieronder. Ontbreekt hij, dan valt de melding terug op de dagnaam alleen: liever geen
// datum dan een verkeerde, want die stuurt de teamleider naar de verkeerde dag.
const dagMetDatum = (dag, datum) => (datum ? `${dag} ${datum}` : DAG_LABELS[dag] || dag);

// Zet de technische redenen[] van een gemeente-signaal om naar één begrijpelijke zin.
// Neemt de eerste reden (prefix-match voor de dynamische die een dag bevatten);
// een onbekende string blijft ongewijzigd als fallback.
function vertaalReden(redenen = []) {
  const r = (redenen || [])[0];
  if (!r) return '';
  if (r.startsWith("dag '") && r.includes('niet in dienst-dagen')) return 'deze dienst draait normaal niet op deze dag';
  if (r === 'niet beschikbaar (isBeschikbaar=false)') return 'is deze dag niet beschikbaar (vrije dag, kantoor, school of andere gemeente)';
  if (r === 'geen_dagdienst op dagdienst') return 'doet geen dagdiensten';
  if (r.startsWith("geen_dagdienst op '")) return 'doet op deze dag geen dagdienst';
  if (r === 'alleen_dagdienst op late/flex (weekdag)') return 'doet doordeweeks geen late dienst';
  if (r.startsWith("geen_late_dienst op '")) return 'doet op deze dag geen late dienst';
  if (r === 'gemeente inactief') return 'deze gemeente staat uit';
  if (r === 'onbekende medewerker') return 'deze persoon bestaat niet meer in de instellingen';
  if (r === 'onbekende dienst') return 'deze dienst bestaat niet meer';
  return r; // fallback: onbekende string ongewijzigd
}

// Inklapbare meldingen-sectie: klikbare kopregel (chevron + titel + teller), standaard OPEN.
// Leeg → rendert helemaal niets, ook de kopregel niet. Dichtgeklapt blijft de kopregel staan.
// Zelfde patroon als de Historie-sectie in Ruilverzoeken.jsx, maar die start dicht.
//
// `sub` = een subsectie BINNEN Waarschuwingen: zelfde gedrag (standaard open, leeg = niets),
// alleen een lichtere kopregel zodat de hiërarchie te zien is. Bewust dezelfde component en
// niet een tweede: de regel "leeg rendert niets" mag maar op één plek staan.
// `startDicht` = de Afgehandeld-sectie. Die is een archief, niet een werklijst: hij hoort
// dicht te beginnen en alleen open te gaan als je er iets in zoekt. Zelfde keuze als de
// Historie-sectie in Ruilverzoeken.jsx:194+370-375.
function MeldingSectie({ titel, aantal, sub = false, startDicht = false, children }) {
  const [open, setOpen] = useState(!startDicht);
  if (aantal === 0) return null;

  return (
    <section className="space-y-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className={sub
          ? 'flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wide hover:text-slate-600'
          : 'flex items-center gap-1.5 text-sm font-semibold text-slate-600 uppercase tracking-wide hover:text-slate-800'}
      >
        {open ? <ChevronDown size={sub ? 14 : 16} /> : <ChevronRight size={sub ? 14 : 16} />}
        {titel} ({aantal})
      </button>
      {open && <div className="space-y-2">{children}</div>}
    </section>
  );
}

// ── Sorteren en samenvoegen ─────────────────────────────────────────────────
// Vaste volgorde binnen elke (sub)sectie: isoJaar → weeknummer → dag (ma→zo) → gemeente →
// dienst. Zonder die volgorde staat de lijst op de volgorde waarin de weken toevallig zijn
// langsgekomen (Object.entries over weekAnalyses in Weekrooster.jsx) en verspringt hij zodra
// die volgorde wijzigt. Niet elke soort draagt alle sleutels — rust en dubbel hebben geen
// gemeente of dienst, reeks geen isoJaar — dus ontbrekende sleutels sorteren ACHTERAAN,
// nooit vooraan.
const DAG_VOLGORDE = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const dagIndex = (dag) => {
  const i = DAG_VOLGORDE.indexOf(dag);
  return i === -1 ? DAG_VOLGORDE.length : i;
};
const getalVgl = (x, y) => {
  const a = Number.isFinite(x) ? x : Number.MAX_SAFE_INTEGER;
  const b = Number.isFinite(y) ? y : Number.MAX_SAFE_INTEGER;
  return a - b;
};
const tekstVgl = (x, y) => {
  if (x === y) return 0;
  if (x == null) return 1;
  if (y == null) return -1;
  return String(x).localeCompare(String(y), 'nl');
};
function vergelijkMelding(a, b) {
  return getalVgl(a.isoJaar, b.isoJaar)
    || getalVgl(a.weeknummer, b.weeknummer)
    || (dagIndex(a.dag) - dagIndex(b.dag))
    || tekstVgl(a.gemeenteKey, b.gemeenteKey)
    || tekstVgl(a.dienst, b.dienst);
}

// "Bram, Jermaine, Kimmy en Leroy" — zelfde vorm als de knelpunten-suggestie
// in analyseKnelpunten, zodat er niet twee manieren van namen aaneenrijgen bestaan.
function namenReeks(namen) {
  return namen.length === 1 ? namen[0] : `${namen.slice(0, -1).join(', ')} en ${namen[namen.length - 1]}`;
}

/**
 * Voegt gemeente-signalen samen die over DEZELFDE cel én dezelfde reden gaan.
 *
 * Dit is de enige soort die wordt samengevoegd. Eén handeling — een dienst die een dag
 * opschuift — levert per persoon een losse regel op die alleen in de NAAM verschilt; vier
 * van die regels zijn één melding met vier namen.
 *
 * De sleutel is het volledige cel-adres PLUS de vertaalde reden. Die reden hoort erbij omdat
 * vertaalReden alleen redenen[0] leest: twee signalen op dezelfde cel met een
 * andere eerste reden krijgen een andere zin en horen dus niet op één regel.
 *
 * `medewerker` is de groepeer-eenheid, niet mwKey: dat veld ontbreekt op de tak 'onbekende
 * medewerker' (analyseer-week.js) en op dubbele signalen. De naam zit er altijd op.
 *
 * `aantal` telt de MELDINGEN die in deze regel zijn opgegaan — de tellers boven de secties
 * moeten meldingen tellen en niet regels, anders lijkt het alsof er meldingen zijn verdwenen.
 */
function groepeerGemeenteSignalen(signalen) {
  const groepen = new Map();
  for (const s of signalen || []) {
    const reden = vertaalReden(s.redenen);
    const sleutel = [s.isoJaar, s.weeknummer, s.dag, s.gemeenteKey, s.dienst, reden].join('|');
    const bestaand = groepen.get(sleutel);
    if (bestaand) {
      bestaand.namen.push(s.medewerker);
      bestaand.aantal += 1;
      continue;
    }
    groepen.set(sleutel, {
      soort: 'gemeente',
      sleutel,
      isoJaar: s.isoJaar,
      weeknummer: s.weeknummer,
      dag: s.dag,
      gemeenteKey: s.gemeenteKey,
      dienst: s.dienst,
      datum: s.datum,
      reden,
      namen: [s.medewerker],
      aantal: 1,
    });
  }
  // Namen alfabetisch binnen één regel; de zin is verder LETTERLIJK die van vóór het
  // samenvoegen (r.108 in de oude versie), op "staat" → "staan" na zodra er meer dan
  // één naam staat.
  return [...groepen.values()].map((g) => {
    const namen = [...g.namen].sort((x, y) => String(x).localeCompare(String(y), 'nl'));
    const werkwoord = namen.length === 1 ? 'staat' : 'staan';
    return {
      ...g,
      namen,
      tekst: `${namenReeks(namen)} ${werkwoord} op ${dagMetDatum(g.dag, g.datum)} in ${gemeenteNaam(g.gemeenteKey)}, maar dat hoort daar niet: ${g.reden}.`,
    };
  });
}

// De afvink-knop rechts op elke balk. Tekst en geen icoon: "Afvinken" en "Terugzetten" zijn
// ondubbelzinnig, en de matrix heeft al drie tekens waar de betekenis uit de vorm moet komen.
// Zelfde stijl als de "Alles wissen"-knop in Ruilverzoeken.jsx:377-379.
// Ontbreekt de sleutel (onvolledig adres, zie meldingsleutel.js), dan komt er geen knop:
// afvinken zou dan nergens landen.
function VinkKnop({ sleutel, afgevinkt, onVink, peil }) {
  if (!sleutel || !onVink) return null;
  return (
    <button
      onClick={() => onVink(sleutel, afgevinkt ? null : peil)}
      className="shrink-0 ml-auto self-start text-xs text-slate-400 hover:text-slate-700 underline-offset-2 hover:underline"
    >
      {afgevinkt ? 'Terugzetten' : 'Afvinken'}
    </button>
  );
}

// "was afgehandeld — situatie is gewijzigd": een vinkje dat is vervallen omdat de peilwaarde
// veranderde. De melding staat dan gewoon weer in zijn eigen subsectie; dit regeltje zegt
// alleen waarom hij terug is.
function VervallenNotitie({ stand }) {
  if (stand !== 'vervallen') return null;
  return (
    <span className="block mt-1 text-xs italic opacity-75">
      was afgehandeld — situatie is gewijzigd
    </span>
  );
}

// "— Jermaine (ziek) en Danny (verlof) tellen niet mee": de namen die wél in de cel staan maar
// die dag afwezig zijn, en dus niet in het bezet-getal ervoor zitten. Zonder deze zin ziet de
// teamleider "0/2" boven een cel waar twee namen in staan en klopt er ogenschijnlijk niets.
//
// Het veld komt van analyseer-week.js (alleen namen) en krijgt zijn type in de renderlaag
// (Weekrooster.jsx, conflictenMetDatum). Ontbreekt het — een conflict uit een oudere opslag, of
// gewoon een cel zonder afwezigen — dan rendert dit niets en oogt de balk precies als voorheen.
//
// namenReeks (r.105) doet het aaneenrijgen; niet een tweede keer opgeschreven, zodat er niet
// twee manieren van "A, B en C" in dit bestand staan.
const AFWEZIG_LABELS = { verlof: 'verlof', ziekte: 'ziek' };

function AfwezigenNotitie({ afwezigen }) {
  if (!afwezigen?.length) return null;
  const delen = afwezigen.map((a) => `${a.naam} (${AFWEZIG_LABELS[a.type] || 'afwezig'})`);
  return (
    <span className="opacity-75">
      {' '}— {namenReeks(delen)} {delen.length === 1 ? 'telt' : 'tellen'} niet mee
    </span>
  );
}

// Eén onderbezettings-balk. Opmaak ongewijzigd; alleen de kleur hangt van de ernst af.
function ConflictBalk({ c, oranje, onVink }) {
  const vak = oranje
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : 'bg-red-50 border-red-200 text-red-800';
  return (
    <div className={`flex items-start gap-2 border rounded-xl px-4 py-3 text-sm ${vak}`}>
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>
        <strong>Week {c.weeknummer}</strong> · {c.gemeente} · {dagMetDatum(c.dag, c.datum)} · {DIENST_LABELS[c.dienst] || c.dienst} —{' '}
        <strong>
          {c.bezet}/{c.nodig} {c.nodig === 1 ? 'persoon' : 'personen'}
        </strong>
        <AfwezigenNotitie afwezigen={c.afwezigen} />
        <VervallenNotitie stand={c.stand} />
      </span>
      <VinkKnop sleutel={c.sleutel} afgevinkt={c.stand === 'afgevinkt'} onVink={onVink} peil={meldingPeil(c)} />
    </div>
  );
}

// Eén signaal-balk (rust/dubbel/gemeente) — altijd amber. Opmaak ongewijzigd.
function SignaalBalk({ r, onVink }) {
  return (
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>
        {r.tekst}
        <VervallenNotitie stand={r.stand} />
      </span>
      <VinkKnop sleutel={r.sleutel} afgevinkt={r.stand === 'afgevinkt'} onVink={onVink} peil={meldingPeil(r)} />
    </div>
  );
}

// reeksSignalen komen van bepaalReeksSignalen (engine) en zijn in Weekrooster verrijkt met
// vanDatum/totDatum — kalenderdatums horen bij de tonende laag, niet bij de engine.
export default function ConflictBanner({
  conflicten, rustSignalen = [], dubbeleSignalen = [], gemeenteSignalen = [], reeksSignalen = [],
  afgevinkt = {}, onVink = null,
}) {
  // Eén balk per conflict (geen dedup over weken); weeknummer staat vooraan.
  // `soort` erop, want de sleutel- en peilfuncties kiezen daarop (lib/meldingsleutel.js).
  const lijst = (conflicten || []).map((c) => ({ ...c, soort: 'onderbezetting' }));

  // ernst bepaalt de kleur; ontbreekt ernst, dan rood (veiligste fallback).
  // rood = 0 of 1 persoon ingepland; oranje = wel 2+, maar nog niet genoeg koppels.
  const problemen = lijst.filter((c) => c.ernst !== 'oranje');
  const oranjeConflicten = lijst.filter((c) => c.ernst === 'oranje');

  // Rust-, dubbel- en gemeente-signalen — getoond, niet geblokkeerd. Mensentaal + oplossing.
  // Deze horen bij de gele bak: het zijn geen harde onderbezettingen.
  //
  // De sorteersleutels (isoJaar/weeknummer/dag/gemeenteKey/dienst) blijven op elke regel
  // staan; ze werden hier eerder weggegooid door een map die alleen `tekst` overhield,
  // waardoor groeperen alleen nog via de zin kon. Nu draagt elke regel zijn eigen adres.
  //
  // ALLEEN gemeente-signalen worden samengevoegd. Rust, dubbel en reeks gaan elk over ÉÉN
  // persoon op ÉÉN moment — rust is één nacht, dubbel is één dag, reeks is één aaneengesloten
  // periode met een eigen begin en eind. Namen daar op één regel zetten haalt de betekenis
  // weg: je ziet dan niet meer wiens reeks waar loopt of wie op welke dag dubbel staat.
  // `soort` staat er expliciet op in plaats van impliciet in de variabelenaam: elke map-tak
  // verwerkt precies één soort, dus de waarde is hier bekend en hoeft nooit uit de zin te
  // worden teruggelezen.
  // mwKey / medewerker gaan mee: die zijn de identiteit in de sleutel (meldingsleutel.js).
  // `laatDag` blijft náást `dag` staan — `dag` is de sorteersleutel, `laatDag` de sleutel.
  const rustRegels = (rustSignalen || []).map((s) => ({
    soort: 'rust',
    mwKey: s.mwKey, medewerker: s.medewerker,
    isoJaar: s.isoJaar, weeknummer: s.weeknummer, dag: s.laatDag, laatDag: s.laatDag,
    tekst: `${s.medewerker}: te weinig rust — late op ${dagMetDatum(s.laatDag, s.datum)} en dagdienst de ochtend erna. Verschuif een van de twee diensten naar een andere dag.`,
  })).sort(vergelijkMelding);

  // `plaatsingen` gaat mee: dat is de peilwaarde. Dezelfde persoon op dezelfde dag kan van
  // twee naar drie diensten gaan, of naar twee ANDERE diensten, zonder dat de sleutel
  // verandert — zonder deze waarde zou een vinkje die nieuwe situatie blijven verbergen.
  const dubbeleRegels = (dubbeleSignalen || []).map((s) => ({
    soort: 'dubbel',
    medewerker: s.medewerker, plaatsingen: s.plaatsingen,
    isoJaar: s.isoJaar, weeknummer: s.weeknummer, dag: s.dag,
    tekst: `${s.medewerker}: staat dubbel op ${dagMetDatum(s.dag, s.datum)} (twee diensten tegelijk). Haal er één weg in de matrix.`,
  })).sort(vergelijkMelding);

  // Slotzin "Controleer de planning voor die dag" is vervallen: met een datum in de zin
  // wijst de melding de dag zelf al aan en was hij dubbelop.
  const gemeenteRegels = groepeerGemeenteSignalen(gemeenteSignalen).sort(vergelijkMelding);

  // isoJaar en lengte gaan mee: isoJaar hoort in de sleutel en werd hier eerder weggegooid,
  // lengte is de peilwaarde (een reeks van 6 naar 7 óf naar 5 dagen laat het vinkje vervallen).
  const reeksRegels = (reeksSignalen || []).map((s) => ({
    soort: 'reeks',
    mwKey: s.mwKey, isoJaar: s.isoJaar, vanWeek: s.vanWeek, vanDag: s.vanDag, lengte: s.lengte,
    weeknummer: s.vanWeek, dag: s.vanDag,
    tekst: `${s.medewerker}: ${s.lengte} dagen achter elkaar ingeroosterd — ${s.vanDatum} t/m ${s.totDatum}`
      + ` (week ${s.vanWeek}${s.totWeek !== s.vanWeek ? ` t/m ${s.totWeek}` : ''}).`
      + ' Dat is te lang aaneengesloten — haal er één dienst tussenuit.',
  })).sort(vergelijkMelding);

  // ── Afvinken: elke regel krijgt zijn sleutel en zijn stand ────────────────
  // `stand` komt uit vinkStand — de enige plek waar de peilwaarde wordt vergeleken. Een
  // 'vervallen' regel staat gewoon weer in zijn eigen subsectie, met een notitie erbij.
  const merk = (regels) => regels.map((r) => {
    const { sleutel, stand } = vinkStand(afgevinkt, r);
    return { ...r, sleutel, stand };
  });
  const isKlaar = (r) => r.stand === 'afgevinkt';
  const telMeldingen = (regels) => regels.reduce((n, r) => n + meldingAantal(r), 0);

  const alleProblemen = merk([...problemen].sort(vergelijkMelding));
  const alleOranje = merk([...oranjeConflicten].sort(vergelijkMelding));
  const alleGemeente = merk(gemeenteRegels);
  const alleReeks = merk(reeksRegels);
  const alleRust = merk(rustRegels);
  const alleDubbel = merk(dubbeleRegels);

  const openProblemen = alleProblemen.filter((r) => !isKlaar(r));
  const openOranje = alleOranje.filter((r) => !isKlaar(r));
  const openGemeente = alleGemeente.filter((r) => !isKlaar(r));
  const openReeks = alleReeks.filter((r) => !isKlaar(r));
  const openRust = alleRust.filter((r) => !isKlaar(r));
  const openDubbel = alleDubbel.filter((r) => !isKlaar(r));

  // Alles wat is afgevinkt, ongeacht soort, in één archief onderaan.
  const afgehandeld = [
    ...alleProblemen, ...alleOranje, ...alleGemeente, ...alleReeks, ...alleRust, ...alleDubbel,
  ].filter(isKlaar);

  // Tellers tellen MELDINGEN, niet regels. Een samengevoegde gemeente-regel noemt alle namen,
  // dus er verdwijnt niets bij het SAMENVOEGEN — daarom telt de gemeente-sectie de signalen
  // zoals ze binnenkwamen (`aantal` per groep), niet het aantal regels.
  //
  // ALLE tellers, ook de koptellers, tellen wat er nog OPEN staat. Hier stond eerst het volle
  // aantal, met de redenering uit 02623f0 dat een dalende teller lijkt alsof er meldingen zijn
  // weggevallen. Die redenering gold voor het SAMENVOEGEN: daar verdween niets en zag je het
  // ook niet terug. Bij AFVINKEN heeft de teamleider de melding zelf weggelegd en staat hij
  // eronder in Afgehandeld — dan hoort de kop wél te dalen, anders staat er "Problemen (4)"
  // boven een lege sectie. Is een sectie helemaal afgevinkt, dan verdwijnt hij:
  // MeldingSectie geeft null bij aantal === 0.
  //
  // De optelling blijft kloppen: open + afgehandeld is nog steeds het volle aantal.
  const aantalProblemen = telMeldingen(openProblemen);
  const aantalWaarschuwingen = openOranje.length
    + telMeldingen(openGemeente) + openReeks.length + openRust.length + openDubbel.length;
  const aantalAfgehandeld = telMeldingen(afgehandeld);

  // Groen alleen als er ECHT niets is — open NOCH afgehandeld.
  //
  // ⚠️ De afgehandeld-check is essentieel. Zonder die derde voorwaarde zou dit groen worden
  // zodra alles is afgevinkt, want de tellers hierboven tellen sinds deze wijziging alleen de
  // OPEN meldingen. "Rooster compleet — de bezetting klopt" zou dan verschijnen terwijl de
  // bezetting juist niet klopt: de melding is alleen weggelegd, niet opgelost.
  //
  // De tekst belooft geen CAO-check meer: die draait niet (controleerCAOGrenzen is bewust
  // uitgeschakeld en geeft altijd []).
  if (aantalProblemen === 0 && aantalWaarschuwingen === 0 && aantalAfgehandeld === 0) {
    return (
      <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm">
        <CheckCircle className="w-4 h-4 shrink-0" />
        <span>Rooster compleet — de bezetting klopt.</span>
      </div>
    );
  }

  // De React-key is overal de MELDINGSLEUTEL. Vier van de vijf subsecties gebruikten de
  // array-index; dat kon zolang een regel geen eigen state had, maar nu er per regel een
  // vinkje aan hangt wijst een index na één verschuiving de verkeerde melding aan.
  // Terugval op de tekst voor een regel zonder sleutel (onvolledig adres): die is niet
  // afvinkbaar, maar moet wel een stabiele key hebben.
  const sleutelVan = (r) => r.sleutel || r.tekst;

  return (
    <div className="space-y-4">
      {/* Problemen krijgt geen subsecties: hier zit maar één soort in (onderbezetting). */}
      <MeldingSectie titel="Problemen" aantal={aantalProblemen}>
        {openProblemen.map((c) => <ConflictBalk key={sleutelVan(c)} c={c} oranje={false} onVink={onVink} />)}
      </MeldingSectie>

      {/* Waarschuwingen in vijf vaste subsecties. Ze staan allemaal OPEN: het doel is een
          KORTERE lijst, niet een verborgen lijst. Een lege subsectie rendert niets, ook geen
          kopregel — die regel staat in MeldingSectie zelf. */}
      <MeldingSectie titel="Waarschuwingen" aantal={aantalWaarschuwingen}>
        <MeldingSectie sub titel="Onderbezetting" aantal={openOranje.length}>
          {openOranje.map((c) => <ConflictBalk key={sleutelVan(c)} c={c} oranje onVink={onVink} />)}
        </MeldingSectie>

        <MeldingSectie sub titel="Staat op een dag die niet klopt" aantal={telMeldingen(openGemeente)}>
          {openGemeente.map((r) => <SignaalBalk key={sleutelVan(r)} r={r} onVink={onVink} />)}
        </MeldingSectie>

        <MeldingSectie sub titel="Te lang aaneengesloten" aantal={openReeks.length}>
          {openReeks.map((r) => <SignaalBalk key={sleutelVan(r)} r={r} onVink={onVink} />)}
        </MeldingSectie>

        <MeldingSectie sub titel="Te weinig rust" aantal={openRust.length}>
          {openRust.map((r) => <SignaalBalk key={sleutelVan(r)} r={r} onVink={onVink} />)}
        </MeldingSectie>

        <MeldingSectie sub titel="Staat dubbel" aantal={openDubbel.length}>
          {openDubbel.map((r) => <SignaalBalk key={sleutelVan(r)} r={r} onVink={onVink} />)}
        </MeldingSectie>
      </MeldingSectie>

      {/* Afgehandeld: alles wat is nagekeken, uit de werklijst maar terugvindbaar. Start
          DICHT — het is een archief. Terugzetten kan hier per regel; de melding gaat dan
          vanzelf terug naar zijn eigen subsectie. */}
      <MeldingSectie titel="Afgehandeld" aantal={aantalAfgehandeld} startDicht>
        {afgehandeld.map((r) => (r.soort === 'onderbezetting'
          ? <ConflictBalk key={sleutelVan(r)} c={r} oranje={r.ernst === 'oranje'} onVink={onVink} />
          : <SignaalBalk key={sleutelVan(r)} r={r} onVink={onVink} />))}
      </MeldingSectie>
    </div>
  );
}
